import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { NetworkGroup, NetworkToken } from '@shared/contract';

import { DemoApiError } from '../api/client';
import { useSession } from '../session/SessionContext';
import {
  clearSelection,
  loadSelection,
  saveSelection,
  type TokenSelection,
} from './storage';

interface SelectionContextValue {
  selection: TokenSelection | null;
  /** Mainnet catalog groups only. */
  networks: NetworkGroup[];
  loadingNetworks: boolean;
  networksError: string | null;
  provisioning: boolean;
  provisionError: string | null;
  /** Resolve a mainnet mint by symbol + chain and provision it. */
  selectMint: (symbol: string, chain: string) => void;
  /** Resolve a mainnet mint by catalog mintId and provision it. */
  selectMintById: (mintId: number) => void;
  /** Clear the active mint (e.g. when currency changes before network is chosen). */
  clearMint: () => void;
  retryNetworks: () => void;
}

const SelectionContext = createContext<SelectionContextValue | null>(null);

function tokenToSelection(
  isTestnet: boolean,
  chain: string,
  token: NetworkToken,
): TokenSelection {
  return {
    isTestnet,
    chain,
    mintId: token.mintId,
    symbol: token.symbol,
    decimals: token.decimals,
    usdPrice: token.usdPrice,
  };
}

/** Restore a saved mainnet selection if it still exists in the catalog. */
function restoreMainnetSelection(
  networks: NetworkGroup[],
  preferred: TokenSelection | null,
): TokenSelection | null {
  if (!preferred || preferred.isTestnet) return null;
  const group = networks.find(
    (n) => !n.isTestnet && n.chain === preferred.chain,
  );
  const token = group?.tokens.find((t) => t.mintId === preferred.mintId);
  if (!group || !token) return null;
  return tokenToSelection(false, group.chain, token);
}

export function SelectionProvider({ children }: { children: ReactNode }) {
  const { session, api, signOut } = useSession();
  const [networks, setNetworks] = useState<NetworkGroup[]>([]);
  const [loadingNetworks, setLoadingNetworks] = useState(false);
  const [networksError, setNetworksError] = useState<string | null>(null);
  const [selection, setSelectionState] = useState<TokenSelection | null>(() =>
    loadSelection(),
  );
  const [provisioning, setProvisioning] = useState(false);
  const [provisionError, setProvisionError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const provisionSeqRef = useRef(0);

  const applySelection = useCallback(
    async (next: TokenSelection) => {
      setSelectionState(next);
      saveSelection(next);
      if (!session) return;

      const seq = ++provisionSeqRef.current;
      setProvisioning(true);
      setProvisionError(null);
      try {
        await api.provision({
          customerId: session.customerId,
          mintId: next.mintId,
        });
        if (seq !== provisionSeqRef.current) return;
      } catch (err) {
        if (seq !== provisionSeqRef.current) return;
        if (err instanceof DemoApiError && err.code === 'INVALID_PASSCODE') {
          clearSelection();
          signOut();
          return;
        }
        const message =
          err instanceof DemoApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Provisioning failed';
        setProvisionError(message);
      } finally {
        if (seq === provisionSeqRef.current) {
          setProvisioning(false);
        }
      }
    },
    [api, session, signOut],
  );

  useEffect(() => {
    if (!session) {
      setNetworks([]);
      setNetworksError(null);
      return;
    }

    let cancelled = false;
    setLoadingNetworks(true);
    setNetworksError(null);

    api
      .getNetworks()
      .then((res) => {
        if (cancelled) return;
        // Demo UI is mainnet-only — drop testnet groups from the catalog.
        const mainnet = res.networks.filter((n) => !n.isTestnet && n.tokens.length > 0);
        setNetworks(mainnet);
        const restored = restoreMainnetSelection(mainnet, loadSelection());
        if (restored) {
          void applySelection(restored);
        } else {
          setSelectionState(null);
          clearSelection();
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof DemoApiError && err.code === 'INVALID_PASSCODE') {
          signOut();
          return;
        }
        const message =
          err instanceof DemoApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Could not load networks';
        setNetworksError(message);
        setNetworks([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingNetworks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session, api, reloadToken, applySelection, signOut]);

  const selectMint = useCallback(
    (symbol: string, chain: string) => {
      const group = networks.find((n) => !n.isTestnet && n.chain === chain);
      const token = group?.tokens.find(
        (t) => t.symbol.toUpperCase() === symbol.toUpperCase(),
      );
      if (!group || !token) return;
      void applySelection(tokenToSelection(false, group.chain, token));
    },
    [networks, applySelection],
  );

  const selectMintById = useCallback(
    (mintId: number) => {
      for (const group of networks) {
        if (group.isTestnet) continue;
        const token = group.tokens.find((t) => t.mintId === mintId);
        if (!token) continue;
        void applySelection(tokenToSelection(false, group.chain, token));
        return;
      }
    },
    [networks, applySelection],
  );

  const clearMint = useCallback(() => {
    provisionSeqRef.current += 1;
    setSelectionState(null);
    clearSelection();
    setProvisioning(false);
    setProvisionError(null);
  }, []);

  const retryNetworks = useCallback(() => {
    setReloadToken((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({
      selection,
      networks,
      loadingNetworks,
      networksError,
      provisioning,
      provisionError,
      selectMint,
      selectMintById,
      clearMint,
      retryNetworks,
    }),
    [
      selection,
      networks,
      loadingNetworks,
      networksError,
      provisioning,
      provisionError,
      selectMint,
      selectMintById,
      clearMint,
      retryNetworks,
    ],
  );

  return (
    <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>
  );
}

export function useSelection(): SelectionContextValue {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection must be used within SelectionProvider');
  return ctx;
}
